import { ApiService } from '../ApiService';
import type { DashboardData } from '../../../types/dashboard';
import type { BusinessUnitFilterBase } from '../../../types/filters';

/**
 * Interface de paramètres pour les requêtes dashboard
 * Supporte le filtrage par unité commerciale (entreprise/succursale/point de vente)
 */
export interface DashboardParams extends BusinessUnitFilterBase {
  period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  fiscalYearId?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Type d'alerte
 */
export type AlertType = 'fiscal_deadline' | 'accounting_error' | 'treasury_alert';

/**
 * Priorité d'alerte
 */
export type AlertPriority = 'high' | 'medium' | 'low';

export const dashboardApi = {
  /**
   * Récupérer les données du tableau de bord
   * @see GET /dashboard
   */
  getDashboardData: (params?: DashboardParams) => 
    ApiService.get<{ success: boolean; data: DashboardData }>('/dashboard', params),

  /**
   * Récupérer les statistiques rapides (KPI)
   * @see GET /dashboard/quick-stats
   */
  getQuickStats: (params?: Pick<DashboardParams, 'fiscalYearId' | 'businessUnitId' | 'businessUnitType'>) => 
    ApiService.get<{ success: boolean; data: DashboardData['quickStats'] }>('/dashboard/quick-stats', params),

  /**
   * Récupérer les ratios financiers
   * @see GET /dashboard/financial-ratios
   */
  getFinancialRatios: (params?: Pick<DashboardParams, 'fiscalYearId' | 'businessUnitId' | 'businessUnitType'>) => 
    ApiService.get<{ success: boolean; data: DashboardData['financialRatios'] }>('/dashboard/financial-ratios', params),
    
  /**
   * Récupérer les indicateurs de performance clés (cote crédit, note financière)
   * @see GET /dashboard/key-performance-indicators
   */
  getKeyPerformanceIndicators: (params?: Pick<DashboardParams, 'fiscalYearId' | 'businessUnitId' | 'businessUnitType'>) => 
    ApiService.get<{ success: boolean; data: DashboardData['keyPerformanceIndicators'] }>('/dashboard/key-performance-indicators', params),

  /**
   * Récupérer les données de revenus pour le graphique
   * @see GET /dashboard/revenue
   */
  getRevenueData: (params?: DashboardParams) => 
    ApiService.get<{ success: boolean; data: DashboardData['revenueData'] }>('/dashboard/revenue', params),

  /**
   * Récupérer les données de dépenses pour le graphique
   * @see GET /dashboard/expenses
   */
  getExpensesData: (params?: DashboardParams) => 
    ApiService.get<{ success: boolean; data: DashboardData['expensesData'] }>('/dashboard/expenses', params),
    
  /**
   * Récupérer les transactions récentes
   * @see GET /dashboard/transactions
   */
  getRecentTransactions: (params?: { 
    limit?: number;
    fiscalYearId?: string;
  } & BusinessUnitFilterBase) => 
    ApiService.get<{ success: boolean; data: DashboardData['recentTransactions'] }>('/dashboard/transactions', params),
    
  /**
   * Récupérer les alertes
   * @see GET /dashboard/alerts
   */
  getAlerts: (params?: { 
    priority?: AlertPriority;
    type?: AlertType;
    fiscalYearId?: string;
  } & BusinessUnitFilterBase) => 
    ApiService.get<{ success: boolean; data: DashboardData['alerts'] }>('/dashboard/alerts', params)
};
